/**
 * The ZIP, on step 1.
 *
 * Asked this early for two reasons: whether we clean there at all, which is a
 * question a customer should not have to reach the address step to hear the
 * answer to; and which days near them are cheaper, which the date step needs
 * before the address exists.
 *
 * It answers coverage only. A nearby saving depends on the day and time, and
 * neither is chosen yet, so all this step says about price is where to look.
 */

import Input from "antd/es/input";
import momentTz from "moment-timezone";
import { Check } from "lucide-react";
import styled from "styled-components";
import { AreaCalendar } from "../../lib/proximity";
import { SAVINGS } from "./savings";

/**
 * Drawn with the app's own `.input` — the 6px radius, pale edge and soft shadow
 * the address fields on step 3 use — so the first field a customer meets looks
 * like every field after it. Only the width is its own, since a ZIP is five
 * characters, and the edge turns red on a problem the way `FormInput`'s does.
 */
const Field = styled.div`
  display: flex;
  flex-direction: column;

  label {
    margin: 0;
  }

  .input.zip-input {
    width: 160px;
    font-size: 16px;
    font-variant-numeric: tabular-nums;
  }

  .input.zip-input.is-invalid {
    border-color: red !important;
  }
`;

/** The line under the field: the answer, or what is wrong, in one place. */
const Status = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 7px;

  svg {
    flex: 0 0 auto;
    color: var(--blue-300);
  }
`;

/** Red and small, exactly as `FormInput` reports a field error. */
const Quiet = styled.span`
  display: block;
  color: #575e75;
`;

const Problem = styled.small`
  display: block;
  color: red;
`;

const Hint = styled.p`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 8px 0 0;
  font-size: 13px;
  line-height: 1.55;
  color: #575e75;
`;

/** A green mark, in the colour the next step prints its savings in. */
const Dot = styled.span`
  flex: 0 0 auto;
  width: 7px;
  height: 7px;
  border-radius: 999px;
  background: ${SAVINGS.mark};
`;

/**
 * Whether any day the calendar will let them pick is cheaper near them. Today
 * is Same-Day only and the calendar ends two months out, so a cheaper day
 * outside that window is not one they can have.
 */
function hasPickableCheaperDay(calendar: AreaCalendar | null): boolean {
  if (calendar?.status !== "served") return false;
  const today = momentTz.tz("America/New_York").format("YYYY-MM-DD");
  const last = momentTz.tz("America/New_York").add(2, "months").format("YYYY-MM-DD");
  return Object.entries(calendar.days).some(
    ([date, day]) => day.tier === "cluster" && date > today && date <= last
  );
}

export function ZipField({
  zip,
  calendar,
  invalid,
  onChange,
  onEnter,
}: {
  zip: string;
  calendar: AreaCalendar | null;
  /** Set when Next was pressed without a usable ZIP. */
  invalid: boolean;
  onChange: (zip: string) => void;
  /** Enter in the field: the same as pressing Next. */
  onEnter?: () => void;
}) {
  const refused = calendar?.status === "refused";
  const served = calendar?.status === "served";
  const unreachable = calendar?.status === "unreachable";
  const checking = zip.length === 5 && calendar === null;
  const hasGreenDays = hasPickableCheaperDay(calendar);

  const problem = refused
    ? calendar.message
    : invalid
    ? "Enter your 5-digit ZIP code."
    : null;

  return (
    <Field>
      <label htmlFor="zip">Your ZIP code</label>
      <Input
        id="zip"
        className={`input zip-input${problem ? " is-invalid" : ""}`}
        size="large"
        inputMode="numeric"
        autoComplete="postal-code"
        maxLength={5}
        placeholder="11215"
        value={zip}
        onChange={(e) => onChange(e.target.value.replace(/\D/g, "").slice(0, 5))}
        onPressEnter={() => onEnter?.()}
        aria-invalid={Boolean(problem) || undefined}
        aria-describedby="zip-status"
      />
      <div id="zip-status" role="status">
        {problem ? (
          <Problem>{problem}</Problem>
        ) : served ? (
          <Status>
            <Check size={16} strokeWidth={2.5} aria-hidden="true" />
            We clean in {zip}.
          </Status>
        ) : checking ? (
          <Quiet>Checking your ZIP…</Quiet>
        ) : unreachable ? (
          /*
            Said, not left blank: a customer should know we could not check
            rather than wonder. They can go on — the address step and checkout
            both check again.
          */
          <Quiet>We couldn’t check your ZIP right now. You can still continue.</Quiet>
        ) : null}
      </div>
      {hasGreenDays ? (
        <Hint>
          <Dot aria-hidden="true" />
          {/* The next step marks those days rather than pricing all of them. */}
          Some days cost less near you — we’ll mark them on the next step.
        </Hint>
      ) : null}
    </Field>
  );
}
